import React, { Component } from "react";
import { connect } from "react-redux";
import { Toastr, Button } from "../../../revvComponents";

import PricebookItemEditor from "../PricebookItemEditor";
import PricingMethodWrapper from "../PricingMethodWrapper";
import FinalWizardScreen from "../FinalWizardScreen";
import PricebookClauseEditor from "../PricebookClauseEditor";

// Actions Import
import PricebooksActions from "../../../actions/PricebooksActions";

// Internal Imports
import {
  createCustomJSON,
  enhanceFormData,
  generatePricingMethodPayload,
  validateItemForm,
  validatePricingMethods,
} from "./methods";
import { PRICEBOOK } from "../../../constants";
import { GlobalUtils } from "../../../utils";

// Destructuring
const {
  PRICEBOOK_FORM_DATA: PB_INFO,
  PRICING_METHOD_FLATPRICE_DATA: PB_PRICE,
  PRICING_METHOD_MATRIX_DATA: PB_MATRIX,
  PRICEBOOK_META_DATA: PB_META,
  PRICEBOOK_CLAUSE_DATA: PB_CLAUSE,
} = PRICEBOOK;
const {
  nukePriceListForm,
  updateFormRowData,
  updateFormRowDataField,
  updatePricebookData,
  updatePricingMethodData,
  getUnusedProducts,
} = PricebooksActions;
const { handleApiErrMsg } = GlobalUtils;

// Declarations
const START_SCREEN = 1;
const END_SCREEN = 4;

class PricebookWizard extends Component {
  state = {
    isEdit: false,
    currentView: 1,
    isSaving: false,
  };

  componentDidMount() {
    const { data, fieldMapIndex } = this.props;
    if (data !== null) {
      // Store gridRowData to Redux , NOTE: Combine the below actions to one
      this.props.updateFormRowData(data);
      // Generate payload to update pricingMethodData in Redux
      const payload = generatePricingMethodPayload(data, fieldMapIndex);
      this.props.updatePricingMethodData(payload);

      // Update the state
      this.setState({ isEdit: true });
    }
    // mkae api call to fetch unused products
    else if (data === null && this.props.pricelistId) {
      this.props.getUnusedProducts(this.props.pricelistId);
    }
  }

  componentWillUnmount() {
    this.props.nukePriceListForm();
  }

  _showWarningToastr = (msg, timeout = 2000) => {
    Toastr.showToastr({
      type: "warning",
      message: msg || "Please fill in the required fields.",
      timeout,
    });
  };

  _isFormFieldsValid = () => {
    const {
      formRowData,
      fieldMapIndex,
      pricingMethodData,
      pbItemEditorRequiredFields,
    } = this.props;
    switch (this.state.currentView) {
      case 1:
        if (
          validateItemForm(
            fieldMapIndex,
            formRowData,
            pbItemEditorRequiredFields,
          ) === "RequiredFieldsWarning"
        ) {
          this._showWarningToastr();
          return false;
        }
        if (validateItemForm(fieldMapIndex, formRowData) === "") {
          return true;
        }
        this._showWarningToastr(
          validateItemForm(fieldMapIndex, formRowData),
          6000,
        );
        return false;
      case 2: {
        // Function to validate for pricingTypes other than Flat Price
        const data = validatePricingMethods(
          pricingMethodData.view,
          pricingMethodData,
          fieldMapIndex,
          formRowData,
        );
        if (data) {
          this.props.updateFormRowDataField(fieldMapIndex.price, data);
          return true;
        }
        return false;
      }
      case 3:
        return true;
      default:
        return false;
    }
  };

  _setView = (view, isNext) => {
    if (isNext) {
      // Page Validations goes here
      if (this._isFormFieldsValid()) this.setState({ currentView: view });
    } else {
      this.setState({ currentView: view });
    }
  };

  _decideView = () => {
    switch (this.state.currentView) {
      case 1:
        return (
          <PricebookItemEditor
            customJSON={createCustomJSON(
              PB_INFO,
              this.props.columnDefs,
              this.props.customColumnDefs,
              this.state.isEdit,
            )}
            isEdit={this.state.isEdit}
            onChange={this._onChange}
            pricebookName={this.props.pricebookName}
            unusedProducts={this.props.unusedProducts}
            data-test="pricebookwizard-itemeditor"
          />
        );
      case 2:
        return (
          <PricingMethodWrapper
            customJSON={createCustomJSON(PB_PRICE, this.props.columnDefs)}
            customMatrixFields={createCustomJSON(
              PB_MATRIX,
              this.props.columnDefs,
            )}
            currencySymbol={this.props.currencySymbol}
            isEdit={this.state.isEdit}
          />
        );
      case 3:
        return (
          <FinalWizardScreen
            customJSON={createCustomJSON(PB_META, this.props.columnDefs)}
            isEdit={this.state.isEdit}
            {...this.props}
          />
        );
      case 4:
        return (
          <PricebookClauseEditor
            customJSON={createCustomJSON(PB_CLAUSE, this.props.columnDefs)}
            isEdit={this.state.isEdit}
            {...this.props}
          />
        );
      default:
        return (
          <PricebookItemEditor
            customJSON={createCustomJSON(
              PB_INFO,
              this.props.columnDefs,
              this.props.customColumnDefs,
            )}
            isEdit={this.state.isEdit}
            onChange={this._onChange}
            data-test="pricebookwizard-default-itemeditor"
          />
        );
    }
  };

  _onSubmit = () => {
    const payload = enhanceFormData(
      this.props.formRowData,
      this.props.apiRequiredFields,
      this.state.isEdit,
      this.props.fieldMapIndex,
    );
    this.props.customColumnDefs.forEach(item => {
      if (
        item.type === "multiSelect" &&
        Array.isArray(payload[item.field]) &&
        payload[item.field].length > 0
      ) {
        let updatedValue = [];

        payload[item.field].forEach(value => {
          if (typeof value === "object") {
            updatedValue.push(value.value);
          } else {
            updatedValue.push(value);
          }
        });
        payload[item.field] = updatedValue;
        updatedValue = [];
      }
    });
    this.setState({ isSaving: true });
    this.props
      .updatePricebookData(payload, this.state.isEdit)
      .then(() => {
        this.setState({ isSaving: false });
        this.props.togglePopUp("showPricebookWizard", false);
        this.props.nukePriceListForm();
        Toastr.showToastr({
          type: "success",
          message: this.state.isEdit
            ? "Product(s) have been updated to the price book successfully."
            : "Product(s) have been added to the price book successfully.",
          timeout: 2000,
        });
      })
      .catch(err => {
        this.setState({ isSaving: false });
        Toastr.showToastr({
          type: "failure",
          message: handleApiErrMsg(err),
          timeout: 3000,
        });
      });
  };

  render() {
    return (
      <div className="addpricebook-wrapper">
        <form className="addpricebook-form">
          <div className="addpricebook-wrapper">{this._decideView()}</div>
        </form>

        <section className="createquotepopup-footer popup-footer clearfix">
          <div className="footer-actions">
            {this.state.currentView > START_SCREEN && (
              <Button
                className="btn btn-transparent createquotepopup-footer-cancelbtn"
                onClick={() => this._setView(this.state.currentView - 1)}
                data-test="pricebookwizard-previous-btn">
                Previous
              </Button>
            )}
            {this.state.currentView < END_SCREEN && (
              <Button
                className="btn btn-primary"
                onClick={() => this._setView(this.state.currentView + 1, "nxt")}
                data-test="pricebookwizard-next-btn">
                Next
              </Button>
            )}

            {this.state.currentView === END_SCREEN && (
              <Button
                isLoading={this.state.isSaving}
                type="submit"
                className="btn-md"
                onClick={this._onSubmit}
                data-test="pricebookwizard-add-btn">
                {this.state.isEdit ? "Update" : "Add"}
              </Button>
            )}
          </div>
        </section>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const {
    formRowData,
    metaData,
    columnDefs,
    customColumnDefs,
    pricingMethodData,
    clauses,
    pbItemEditorRequiredFields,
  } = state.PricebookStore;
  return {
    formRowData,
    fieldMapIndex: metaData.fieldMapIndex,
    apiRequiredFields: metaData.apiRequiredFields,
    columnDefs,
    customColumnDefs,
    pricingMethodData,
    clauses,
    pbItemEditorRequiredFields,
  };
};

const mapDispatchToProps = {
  nukePriceListForm,
  updateFormRowData,
  updateFormRowDataField,
  updatePricebookData,
  updatePricingMethodData,
  getUnusedProducts,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricebookWizard);
